import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Spinner, Alert, Badge } from 'react-bootstrap';
import { Search, CreditCard, CheckCircle, Download, RefreshCw, Mail, Calendar, FileText, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const config = require('../config.json');

const ApplicationStatusChecker = () => {
  // Search inputs
  const [appNo, setAppNo] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [email, setEmail] = useState('');

  // Processing states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [applicationData, setApplicationData] = useState(null);

  // PhonePe States
  const [isInitiatingPhonePe, setIsInitiatingPhonePe] = useState(false);
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const navigate = useNavigate();
  const clientID = 'MDUEDN';
  const searchTopic = 'JobPayment';

  useEffect(() => {
    const storedApp = getStoredApplication();
    if (storedApp && storedApp.applicationID) {
      setApplicationData(storedApp);
      setAppNo(storedApp.applicationID || '');
      setEmail(storedApp.emailID || '');
      setDateOfBirth(storedApp.dateOfBirth || '');
    }
  }, []);

  const getStoredApplication = () => {
    try {
      const saved = sessionStorage.getItem('currentApplication');
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  };

  // Fetch Application Details
  const handleFetchStatus = async (e) => {
    e.preventDefault();
    if (!appNo || !dateOfBirth || !email) {
      setError("Please fill all required search fields.");
      return;
    }

    setLoading(true);
    setError(null);
    setApplicationData(null);
    setErrorMessage('');
    setStatusMessage('');

    try {
      const response = await axios.get(
        `${config.api.invokeURL}/staff/applications?fetchKey=${appNo.trim()}&fetchOption=${searchTopic}&clientID=${clientID}&searchDOB=${dateOfBirth}&searchEmail=${email.trim()}`,
        {
          headers: { 'x-api-key': config.api.key }
        }
      );

      if (response.data && response.data.length > 0) {
        const item = response.data[0];
        item.paymentMode = 'phonepe';
        item.paymentAmount = '200';
        item.schoolID = item.appliedSchoolID;
        setApplicationData(item);
        sessionStorage.setItem('currentApplication', JSON.stringify(item));
      } else {
        setError('No application found matching the provided Application Number, Email, and Date of Birth.');
      }
    } catch (err) {
      console.error('Fetch Status Error:', err);
      setError(err?.response?.data?.message || 'Application not found or connection issue. Please verify your details.');
    } finally {
      setLoading(false);
    }
  };

  // Initiate PhonePe Payment for Pending Application
  const handlePhonePePayment = async () => {
    if (isInitiatingPhonePe || isProcessingOrder || !applicationData) return;

    try {
      setErrorMessage('');
      setStatusMessage('Connecting to PhonePe Secure Gateway...');
      setIsInitiatingPhonePe(true);

      const apiBase = config?.api?.invokeURL;
      const orderAmount = 200;
      const todayDate = new Date().toISOString();

      const params = {
        clientID: clientID,
        jobPostingID: applicationData.jobPostingID || 'AIDED_JOB',
        orderId: applicationData.applicationID,
        orderRefId: applicationData.applicationID,
        referenceId: 'NA',
        orderType: 'New',
        txStatus: applicationData.applicationStatus || 'Pending Payment',
        paymentMode: 'phonepe',
        txMsg: 'Application Fee Payment',
        txTime: todayDate,
        orderAmount: orderAmount.toString(),
        pocName: applicationData.applicantName,
        pocEmail: applicationData.emailID,
        pocPhone: applicationData.phoneNumber,
        pocAddress: applicationData.currentAddress,
        transactionDate: todayDate,
        discountAmount: 0,
        discountValue: 0,
        discountCode: 'NA',
        subTotal: orderAmount,
        gstCharges: 0,
        paymentPartner: 'phonepe',
        cart: "Application Fee",
      };

      const res = await axios.post(`${apiBase}/transaction/phonepev2/checkout`, params, {
        headers: { 'x-api-key': config.api.key }
      });

      const temp = { ...applicationData, merchantOrderId: res.data.merchantOrderId, clientID: clientID };
      sessionStorage.setItem('currentApplication', JSON.stringify(temp));

      const redirectUrl = res.data?.phonepeResponse?.redirectUrl;
      if (!redirectUrl) {
        throw new Error('PhonePe redirect URL missing in gateway response.');
      }

      const callback = async (response) => {
        if (response === 'USER_CANCEL') {
          setErrorMessage('Payment cancelled by user.');
          setIsInitiatingPhonePe(false);
          return;
        }

        if (response === 'CONCLUDED') {
          try {
            setIsProcessingOrder(true);
            setStatusMessage('Payment confirmed. Verifying status...');
            await handleOrderStatusUpdate(temp);
            navigate(`/application/payment-status?orderId=${temp.applicationID}&merchantOrderId=${temp.merchantOrderId}`);
          } catch (err) {
            setErrorMessage('Payment succeeded, but verification took longer than expected. Please check your status again.');
            setIsProcessingOrder(false);
          }
        }
      };

      if (window?.PhonePeCheckout?.transact && typeof window.PhonePeCheckout.transact === 'function') {
        window.PhonePeCheckout.transact({ tokenUrl: redirectUrl, callback, type: 'IFRAME' });
      } else {
        window.location.href = redirectUrl;
      }
    } catch (error) {
      console.error('PhonePe initiation error:', error);
      setErrorMessage(error?.response?.data?.message || error?.message || 'Unable to start PhonePe transaction.');
      setIsInitiatingPhonePe(false);
      setIsProcessingOrder(false);
    } finally {
      if (!isProcessingOrder) {
        setIsInitiatingPhonePe(false);
      }
    }
  };

  const handleOrderStatusUpdate = async (tempApp) => {
    if (!tempApp?.merchantOrderId) return;
    try {
      const apiBase = config?.api?.invokeURL;
      const res = await axios.get(`${apiBase}/transaction/phonepev2/order-status?merchantOrderId=${tempApp.merchantOrderId}`, {
        headers: { 'x-api-key': config.api.key }
      });
      if (res.data && res.data.status) {
        tempApp.applicationStatus = res.data.status;
        sessionStorage.setItem('currentApplication', JSON.stringify(tempApp));
        setApplicationData({ ...tempApp });
      }
    } catch (err) {
      console.error('Order status update error:', err);
    }
  };

  const isPaid = applicationData?.applicationStatus === 'Success' || applicationData?.applicationStatus === 'SUCCESS';
  const isPending = !isPaid;

  return (
    <Container className="py-4" style={{ minHeight: '80vh' }}>
      <Row className="justify-content-center">
        <Col lg={8} md={10}>

          {/* Search Card */}
          <Card className="border-0 shadow-soft p-3 p-md-4 rounded-4 mb-4 bg-white">
            <Card.Body>
              <div className="d-flex align-items-center gap-3 mb-4 border-bottom pb-3">
                <div className="bg-primary bg-opacity-10 text-primary p-3 rounded-3">
                  <Search size={24} />
                </div>
                <div>
                  <h4 className="fw-bold mb-0 text-dark">Track Application & Settle Fee</h4>
                  <p className="text-muted small mb-0">Enter your application credentials to check verification or complete payment</p>
                </div>
              </div>

              <Form onSubmit={handleFetchStatus}>
                <Row className="g-3">
                  <Col sm={4}>
                    <Form.Group controlId="appNoInput">
                      <Form.Label className="small fw-600 text-dark">Application ID <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. IDN-2026-101"
                        value={appNo}
                        onChange={(e) => setAppNo(e.target.value)}
                        required
                        className="py-2"
                      />
                    </Form.Group>
                  </Col>

                  <Col sm={4}>
                    <Form.Group controlId="emailInput">
                      <Form.Label className="small fw-600 text-dark">Registered Email <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="name@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="py-2"
                      />
                    </Form.Group>
                  </Col>

                  <Col sm={4}>
                    <Form.Group controlId="dateOfBirthInput">
                      <Form.Label className="small fw-600 text-dark">Date of Birth <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="date"
                        value={dateOfBirth}
                        onChange={(e) => setDateOfBirth(e.target.value)}
                        required
                        className="py-2"
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={12}>
                    <Button
                      type="submit"
                      variant="primary"
                      className="w-100 py-2.5 rounded-pill d-flex align-items-center justify-content-center gap-2 mt-2 shadow-sm"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" />
                          <span>Searching...</span>
                        </>
                      ) : (
                        <>
                          <Search size={18} /> Retrieve Application Details
                        </>
                      )}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>

          {/* Search Error Alert */}
          {error && (
            <Alert variant="danger" className="rounded-3 shadow-sm mb-4 d-flex align-items-center gap-2">
              <AlertCircle size={20} className="flex-shrink-0" />
              <span>{error}</span>
            </Alert>
          )}

          {/* Application Details Card */}
          {applicationData && (
            <Card className="border-0 shadow-soft p-3 p-md-4 rounded-4 bg-white">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-3">
                  <div>
                    <h5 className="fw-bold text-dark mb-0">Application Overview</h5>
                    <span className="text-muted small">Application ID: {applicationData.applicationID}</span>
                  </div>
                  <Badge bg={isPaid ? "success" : "warning"} className="fs-6 px-3 py-2 rounded-pill">
                    {isPaid ? "Payment Completed" : "Pending Payment"}
                  </Badge>
                </div>

                <Table borderless responsive className="mb-4 align-middle text-start">
                  <tbody>
                    <tr>
                      <td className="text-muted ps-0 py-1.5 small">Applicant Name:</td>
                      <td className="fw-bold text-dark text-end pe-0">{applicationData.applicantName}</td>
                    </tr>
                    <tr>
                      <td className="text-muted ps-0 py-1.5 small">Applied Post & Subject:</td>
                      <td className="fw-600 text-dark text-end pe-0">{applicationData.appliedPost} - {applicationData.appliedSubject}</td>
                    </tr>
                    <tr>
                      <td className="text-muted ps-0 py-1.5 small">Target School:</td>
                      <td className="fw-600 text-dark text-end pe-0">{applicationData.schoolName}</td>
                    </tr>
                    <tr>
                      <td className="text-muted ps-0 py-1.5 small">Application Fee:</td>
                      <td className="fw-bold text-primary text-end pe-0">₹ 200.00</td>
                    </tr>
                    {applicationData.merchantOrderId && (
                      <tr>
                        <td className="text-muted ps-0 py-1.5 small">Transaction Reference:</td>
                        <td className="text-muted font-monospace text-end pe-0 small">{applicationData.merchantOrderId}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                {/* State: Pending Payment */}
                {isPending && (
                  <div className="border border-warning-subtle bg-warning bg-opacity-10 rounded-4 p-4 text-center mb-3">
                    <CreditCard size={36} className="text-warning mx-auto mb-2" />
                    <h5 className="fw-bold text-dark">Payment Required</h5>
                    <p className="text-muted small mx-auto mb-3" style={{ maxWidth: '450px' }}>
                      Your application has been registered. Please settle the processing fee of ₹200 via PhonePe to finalize your submission.
                    </p>

                    <Button
                      variant="primary"
                      size="lg"
                      className="px-5 py-2.5 rounded-pill shadow-sm d-flex align-items-center justify-content-center gap-2 mx-auto fw-bold"
                      onClick={handlePhonePePayment}
                      disabled={isInitiatingPhonePe || isProcessingOrder}
                    >
                      {isInitiatingPhonePe || isProcessingOrder ? (
                        <>
                          <Spinner animation="border" size="sm" />
                          <span>{isProcessingOrder ? 'Verifying...' : 'Connecting to PhonePe...'}</span>
                        </>
                      ) : (
                        <>
                          <CreditCard size={18} /> Pay ₹200 via PhonePe
                        </>
                      )}
                    </Button>

                    {statusMessage && <Alert variant="info" className="mt-3 py-2 small rounded-3 mb-0">{statusMessage}</Alert>}
                    {errorMessage && <Alert variant="danger" className="mt-3 py-2 small rounded-3 mb-0">{errorMessage}</Alert>}
                  </div>
                )}

                {/* State: Paid & Complete */}
                {isPaid && (
                  <div className="border border-success-subtle bg-success bg-opacity-10 rounded-4 p-4 text-center mb-3">
                    <CheckCircle size={40} className="text-success mx-auto mb-2" />
                    <h5 className="fw-bold text-success">Application Fully Submitted</h5>
                    <p className="text-muted small mx-auto mb-3" style={{ maxWidth: '450px' }}>
                      Your application processing fee has been received. You can now download or print your application summary and payment receipt.
                    </p>
                    <div className="d-flex justify-content-center gap-2">
                      <Button
                        variant="success"
                        className="px-4 py-2 rounded-pill d-flex align-items-center gap-2 shadow-sm"
                        onClick={() => window.print()}
                      >
                        <Download size={16} /> Print Application Receipt
                      </Button>
                    </div>
                  </div>
                )}

              </Card.Body>
            </Card>
          )}

        </Col>
      </Row>
    </Container>
  );
};

export default ApplicationStatusChecker;